import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import type { SelectionScopeDto } from '@dealstack/shared/lib/backendClient';
import type { SelectionState, TransactionSelection } from './TransactionSelection.types';
import { sameSelectionScope } from './transactionSelection.utils';

const EMPTY: SelectionState = { ids: new Set(), scope: null };

export const TransactionSelectionContext = createContext<TransactionSelection | null>(null);

interface Props {
	children: ReactNode;
}

/** Holds the batch's row selection for the transactions panel and the status bar's confirm. */
export const TransactionSelectionProvider = ({ children }: Props) => {
	const [state, setState] = useState<SelectionState>(EMPTY);

	const toggle = useCallback((rowId: string, scope: SelectionScopeDto) => {
		setState((current) => {
			const sameScope = current.scope !== null && sameSelectionScope(current.scope, scope);
			const ids = new Set(sameScope ? current.ids : []);
			if (ids.has(rowId)) {
				ids.delete(rowId);
			} else {
				ids.add(rowId);
			}
			return ids.size === 0 ? EMPTY : { ids, scope };
		});
	}, []);

	const selectAll = useCallback((rowIds: string[], scope: SelectionScopeDto) => {
		setState(rowIds.length === 0 ? EMPTY : { ids: new Set(rowIds), scope });
	}, []);

	const deselect = useCallback((rowIds: readonly string[]) => {
		setState((current) => {
			if (!rowIds.some((rowId) => current.ids.has(rowId))) {
				return current;
			}
			const ids = new Set(current.ids);
			rowIds.forEach((rowId) => ids.delete(rowId));
			return ids.size === 0 ? EMPTY : { ids, scope: current.scope };
		});
	}, []);

	const clear = useCallback(() => setState(EMPTY), []);

	const value = useMemo<TransactionSelection>(
		() => ({
			selectedIds: state.ids,
			scope: state.scope,
			isSelected: (rowId) => state.ids.has(rowId),
			toggle,
			selectAll,
			deselect,
			clear,
		}),
		[state, toggle, selectAll, deselect, clear]
	);

	return (
		<TransactionSelectionContext.Provider value={value}>
			{children}
		</TransactionSelectionContext.Provider>
	);
};
